import store from "@/store/store";
import { canCreatePost, hasChangeConfigPermission } from "@/includes/PermissionHelper";
import { TestBoardStateCode } from "@/includes/types/Board.types";
import { CalendarPostsSource } from "@/includes/types/CalendarPostsSource";

import { EntityTypes } from "piramis-base-components/src/components/SelectEntityWizard/includes/types";
import TokenId from "piramis-base-components/src/shared/utils/TokenId";
import {
    TemplateMenuConfigItem
} from "piramis-base-components/src/plugins/AdminTemplate/NewConfig/types/TemplateMenuConfig.types";
import { BaseTemplateConfig, MenuItemTypeEnum } from "piramis-base-components/src/plugins/AdminTemplate/NewConfig";

import { Guid } from "guid-typescript";

const boardsDynamicHeader = (): Array<TemplateMenuConfigItem> => {
    return store.getters.sortedBoards.map(board => {
        const boardId = TokenId.getId(board.board)

        return {
            type: MenuItemTypeEnum.DynamicHeader,
            isVisible: [
                (ctx) => !!ctx && !ctx.route.params[EntityTypes.BOT_ID] && !ctx.route.params[EntityTypes.CHANNEL_ID],
            ],
            title: () => board.title,
            img: () => board.avatar,
            route: {
                name: 'posts_planner',
                params: {
                    [EntityTypes.BOT_ID]: boardId
                },
                query: {
                    ...!canCreatePost(board.permissions) ?
                        { calendar: CalendarPostsSource.Suggested } :
                        { calendar: CalendarPostsSource.Schedule }
                }
            }
        }
    })
}

export const sidebar: BaseTemplateConfig['sidebar'] = [
    {
        type: MenuItemTypeEnum.Route,
        route: {
            name: "Boards",
        },
        icon: {
            iconType: "feather",
            name: 'icon-layout'
        },
    },
    boardsDynamicHeader,
    {
        type: MenuItemTypeEnum.DynamicHeader,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
        ],
        title: (ctx) => {
            if (store.state.boardsState.activeBoard?.board) {
                return store.state.boardsState.activeBoard?.title ?? ''
            }

            return ''
        },
        img: (ctx) => {
            if (store.state.boardsState.activeBoard?.board) {
                const currentBoard = store.state.boardsState.activeBoard

                if (currentBoard) {
                    return currentBoard.avatar
                }
            }

            return ''
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        icon: {
            iconType: 'feather',
            name: 'icon-settings',
        },
        route: {
            name: 'Common_settings'
        },
        class: [
            () => {
                if (store.state.boardsState.activeBoard) {
                    return [ TestBoardStateCode.ApiError, TestBoardStateCode.BotDomainError ].includes(store.state.boardsState.activeBoard.test_state)
                        ? 'bot-state-icon'
                        : ''
                } else {
                    return ''
                }
            },
            () => {
                if (!store.getters.hasBoardLicense) {
                    return 'bot-license-icon'
                } else {
                    return ''
                }
            }
        ],
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        icon: {
            name: 'icon-dollar-sign',
            iconType: 'feather',
        },
        route: {
            name: "OffersList",
        },
        tags: [
            {
                text: "New",
                color: 'rgba(var(--a-success))'
            }
        ],
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx): boolean => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'Channels',
        },
        icon: {
            name: 'icon-radio',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: "Bot_feedback",
        },
        icon: {
            name: 'icon-message-square',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
        ],
        route: {
            name: "posts_planner"
        },
        icon: {
            name: 'icon-calendar',
            iconType: "feather",
        }
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'Distributions'
        },
        icon: {
            name: 'icon-rss',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        route: {
            name: 'Users_options',
        },
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        icon: {
            name: 'icon-users',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            () => hasChangeConfigPermission(),
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
        ],
        route: {
            name: 'Flows_List',
        },
        tags: [
            () => {
                if (store.state.boardsState.activeBoard?.license_expired) {
                    return {
                        text: "Prime",
                        color: '#f75357'
                    }
                }

                return null
            }
        ],
        icon: {
            name: 'icon-git-pull-request',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            () => hasChangeConfigPermission(),
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => {
                const board = store.state.boardsState?.activeBoard

                if (board) {
                    const labels = board.config.labels
                    const properties = board.properties

                    return !!Object.keys(labels).length || properties.length > 0
                }

                return false
            },
        ],
        route: {
            name: 'Bot_Properties',
        },
        icon: {
            name: 'icon-archive',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            () => hasChangeConfigPermission(),
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => store.getters.hasCashRegisters,
        ],
        route: {
            name: 'Shop',
        },
        icon: {
            name: 'icon-shopping-cart',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => canCreatePost(),
            () => store.getters.hasDrafts,
        ],

        route: {
            name: 'Drafts_list',
        },
        icon: {
            name: 'icon-folder',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
            () => {
                const board = store.state.boardsState?.activeBoard

                if (board) {
                    const styles = board.config.styles

                    return !!styles && !!Object.keys(styles).length
                }

                return false
            },
        ],
        route: {
            name: 'Styles',
        },
        icon: {
            name: 'icon-layers',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => store.getters.hasForms,
        ],
        icon: {
            name: 'icon-edit',
            iconType: 'feather',
        },
        route: {
            name: 'Forms',
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && !ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
            () => store.getters.hasArticles,
        ],
        route: {
            name: 'articles_list',
        },
        icon: {
            name: 'icon-file-text',
            iconType: 'feather'
        }
    },
    {
        type: MenuItemTypeEnum.DynamicHeader,
        route: { name: 'Channel_main_settings' },
        isVisible: [
            (ctx) => !!ctx && !!ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission()
        ],
        title: (ctx) => store.state.channelsState.activeChannel ? store.state.channelsState.activeChannel.title : '',
        img: (ctx) => store.state.channelsState.activeChannel ? store.state.channelsState.activeChannel.avatar : '',
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => store.getters.isBoardSet,
            (ctx) => !!ctx && ctx.route.params[EntityTypes.CHANNEL_ID],
        ],
        route: {
            name: "posts_planner"
        },
        icon: {
            name: 'icon-calendar',
            iconType: "feather",
        }
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission()
        ],
        route: {
            name: 'Channel_main_settings',
        },
        icon: {
            name: 'icon-radio',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission()
        ],
        route: {
            name: 'Channel_comments',
        },
        icon: {
            iconType: "feather",
            name: 'icon-message-circle',
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'Publish_settings',
        },
        icon: {
            name: 'icon-send',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'Channel_protection',
        },
        icon: {
            name: 'icon-shield',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'approve_balancer',
        },
        icon: {
            name: 'icon-sliders',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        route: {
            name: 'Channel_face_control',
        },
        icon: {
            name: 'icon-eye',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission()
        ],
        route: {
            name: 'Channel_subscription',
        },
        icon: {
            name: 'icon-users',
            iconType: "feather"
        },
    },
    {
        type: MenuItemTypeEnum.Route,
        route: {
            name: 'invite_links_list',
        },
        isVisible: [
            (ctx) => !!ctx && !!ctx.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission()
        ],
        icon: {
            name: 'icon-link',
            iconType: 'feather'
        },
    },
    {
        type: MenuItemTypeEnum.Dropdown,
        guid: Guid.create().toString(),
        alias: 'menu_statistics_all_title',
        isVisible: [
            (ctx) => !!ctx?.route.params[EntityTypes.CHANNEL_ID],
            () => hasChangeConfigPermission(),
        ],
        icon: {
            name: 'icon-bar-chart',
            iconType: "feather"
        },
        children: [
            {
                type: MenuItemTypeEnum.Route,
                icon: {
                    name: 'icon-bar-chart',
                    iconType: "feather",
                },
                route: {
                    name: 'Channel_statistics',
                },
            },
            {
                type: MenuItemTypeEnum.Route,
                route: {
                    name: 'Invite_links_statistics',
                },
                icon: {
                    name: 'icon-bar-chart',
                    iconType: "feather"
                },
            },
        ],
    },
]